/**
 * SSO 로그인 페이지
 * 
 * @description
 * KHA 등 외부 SSO 제공자에서 리다이렉트된 사용자를 자동으로 로그인 처리
 * 정보 제공 동의 화면 없이 즉시 SSO 로그인 API 호출
 * 
 * @route /sso/:origin/login/:userSsoAccessId
 * 
 * @example
 * /sso/kha/login/26
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/data/queries/useUser';
import { getSsoInfo, ssoLogin } from '@/services/ssoService';
import { AlertDialog } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { convertRoleNumberToString, resolveLicenseFromProfile } from '@/utils/roleUtils';
import type { SsoOrigin } from '@/types/api/sso';

export default function SsoLogin(): React.ReactElement {
  const { origin, userSsoAccessId } = useParams<{
    origin: SsoOrigin;
    userSsoAccessId: string;
  }>();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { getProfile } = useUser();

  const [isLoading, setIsLoading] = useState(true);
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    message: string;
  }>({
    isOpen: false,
    message: '',
  });

  const showAlert = useCallback((message: string) => {
    setAlertState({ isOpen: true, message });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertState({ isOpen: false, message: '' });
  }, []);

  useEffect(() => {
    const handleSsoLogin = async () => {
      // URL 파라미터 검증
      if (!origin || origin !== 'kha') {
        showAlert('지원하지 않는 SSO 제공자입니다.');
        setIsLoading(false);
        return;
      }

      if (!userSsoAccessId || isNaN(Number(userSsoAccessId))) {
        showAlert('잘못된 SSO 접근 정보입니다.');
        setIsLoading(false);
        return;
      }

      const userSsoAccessIdNum = Number(userSsoAccessId);

      try {
        // 1. UserSsoAccess 정보 조회
        const ssoInfo = await getSsoInfo(userSsoAccessIdNum);

        // 2. SSO 로그인 API 호출
        const { accessToken, refreshToken, userId } = await ssoLogin({
          userSsoAccessId: userSsoAccessIdNum,
        });

        // 3. 토큰 저장
        localStorage.setItem('medicrew_access_token', accessToken);
        if (refreshToken?.trim()) {
          localStorage.setItem('medicrew_refresh_token', refreshToken.trim());
        } else {
          localStorage.removeItem('medicrew_refresh_token');
        }

        // 4. 프로필 조회하여 AuthContext 업데이트
        const userProfile = await getProfile();

        const userRole = convertRoleNumberToString(userProfile.role);
        const userLicense = resolveLicenseFromProfile(userProfile);

        const organizationId = userProfile.organizationId != null ? String(userProfile.organizationId) : null;
        const organizationName = userProfile.organizationName?.trim() ?? '';

        const displayName =
          userProfile.realName && userProfile.realName.trim() ? userProfile.realName.trim() : ssoInfo.user?.loginId ?? '';

        const currentAccountEntry = {
          accountId: `acc_${userProfile.userId}`,
          loginId: ssoInfo.user?.loginId ?? '',
          displayName,
          email: userProfile.email || '',
          profileImageUrl: userProfile.profileImageUrl,
          phoneNumber: userProfile.phoneNumber?.trim() ?? '',
          affiliation: userProfile.affiliation?.trim() ?? '',
          position: userProfile.position?.trim() ?? '',
          department: userProfile.department?.trim() ?? '',
          organizationId,
          organizationName,
          role: userRole,
          license: userLicense,
          isActive: true,
        };

        // 이전 계정 목록 유지
        let accounts: typeof currentAccountEntry[] = [currentAccountEntry];
        try {
          const savedUser = localStorage.getItem('medicrew_user');
          if (savedUser) {
            const parsed = JSON.parse(savedUser);
            if (parsed?.accounts && Array.isArray(parsed.accounts) && parsed.accounts.length > 0) {
              const savedAccounts = parsed.accounts as typeof currentAccountEntry[];
              const others = savedAccounts.filter((acc: { loginId?: string }) => acc.loginId !== ssoInfo.user?.loginId);
              accounts = [
                currentAccountEntry,
                ...others.map((acc) => ({
                  ...acc,
                  isActive: false,
                })),
              ];
            }
          }
        } catch {
          // localStorage 파싱 실패 시 현재 계정만 사용
        }

        const guestAccountExpiresAt =
          userRole === 'guest' ? (userProfile.guestAccountExpiresAt ?? null) : null;

        const userData = {
          userId: userProfile.userId,
          name: displayName,
          email: userProfile.email || '',
          profileImageUrl: userProfile.profileImageUrl,
          guestAccountExpiresAt,
          isAuthenticated: true,
          currentAccount: {
            accountId: currentAccountEntry.accountId,
            loginId: currentAccountEntry.loginId,
            displayName: currentAccountEntry.displayName,
            email: currentAccountEntry.email,
            profileImageUrl: currentAccountEntry.profileImageUrl,
            phoneNumber: currentAccountEntry.phoneNumber,
            affiliation: currentAccountEntry.affiliation,
            position: currentAccountEntry.position,
            department: currentAccountEntry.department,
            organizationId: currentAccountEntry.organizationId,
            organizationName: currentAccountEntry.organizationName,
            role: currentAccountEntry.role,
            license: currentAccountEntry.license,
          },
          accounts,
        };
        authLogin(userData);

        // 5. 리다이렉트 처리
        if (ssoInfo.userSsoAccess.redirect?.classId) {
          // classId가 있으면 클래스 상세 페이지로 이동
          const classId = ssoInfo.userSsoAccess.redirect.classId;
          navigate(`/open-class/${classId}`);
        } else {
          // 없으면 홈으로 이동
          navigate(ROUTES.HOME);
        }
      } catch (error: any) {
        showAlert(error.message || 'SSO 로그인에 실패했습니다.');
        setIsLoading(false);
      }
    };

    handleSsoLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      {isLoading ? (
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent mb-4"></div>
          <p className="text-gray-600 text-lg">로그인 중...</p>
        </div>
      ) : null}

      <AlertDialog isOpen={alertState.isOpen} onClose={closeAlert} message={alertState.message} />
    </div>
  );
}
